import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle } from "lucide-react";

/**
 * Embeds the Tawk.to live chat behind a CUSTOM iridescent floating button.
 *
 * - The default Tawk launcher bubble is hidden; we render our own animated,
 *   iridescent FAB that opens the chat (Tawk_API.maximize).
 * - On ready we detect the visitor's COUNTRY (by IP, via the free ipwho.is
 *   service) and LANGUAGE (i18n / browser) and push them to Tawk as visitor
 *   attributes so the support agent sees where the person connects from.
 *
 * Mounted ONLY inside the authenticated area (DashboardLayout).
 * Widget ID override via REACT_APP_TAWK_ID + REACT_APP_TAWK_KEY in .env.
 */
const DEFAULT_TAWK_ID  = "6a0b2a32bfcbed1c343e26e2";
const DEFAULT_TAWK_KEY = "1jotppll7";

async function detectCountry() {
  try {
    const r = await fetch("https://ipwho.is/", { cache: "no-store" });
    const d = await r.json();
    if (d && d.success !== false) {
      return {
        country: d.country || "",
        code: d.country_code || "",
        flag: (d.flag && d.flag.emoji) || "",
      };
    }
  } catch { /* offline / blocked — skip silently */ }
  return { country: "", code: "", flag: "" };
}

function pushVisitorAttributes(language) {
  detectCountry().then(({ country, code, flag }) => {
    const attrs = {
      Pais: country ? `${flag} ${country} (${code})`.trim() : "Desconocido",
      Idioma: (language || "").toUpperCase() || "—",
    };
    try {
      window.Tawk_API?.setAttributes?.(attrs, function () { /* noop */ });
    } catch { /* ignore */ }
  });
}

export default function TawkChat() {
  const { i18n } = useTranslation();
  const [maximized, setMaximized] = useState(false);
  // Draggable position (persisted). null = use default CSS bottom-right placement.
  const [pos, setPos] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("op_chat_fab_pos"));
      if (s && typeof s.x === "number" && typeof s.y === "number") return s;
    } catch { /* ignore */ }
    return null;
  });
  const drag = useRef({ active: false, moved: false, offsetX: 0, offsetY: 0, startX: 0, startY: 0 });
  const posRef = useRef(pos);
  posRef.current = pos;

  useEffect(() => {
    const tawkId  = process.env.REACT_APP_TAWK_ID  || DEFAULT_TAWK_ID;
    const tawkKey = process.env.REACT_APP_TAWK_KEY || DEFAULT_TAWK_KEY;
    const lang = i18n.language || navigator.language || "es";
    let cancelled = false;

    const onReady = () => {
      if (cancelled) return;
      // Keep Tawk's default bubble hidden and force-close any auto-opened window —
      // the chat must ONLY open when the user clicks our FAB.
      try { window.Tawk_API.minimize && window.Tawk_API.minimize(); } catch { /* ignore */ }
      try { window.Tawk_API.hideWidget(); } catch { /* ignore */ }
      setMaximized(false);
      window.Tawk_API.onChatMaximized = () => setMaximized(true);
      window.Tawk_API.onChatMinimized = () => {
        setMaximized(false);
        try { window.Tawk_API.hideWidget(); } catch { /* ignore */ }
      };
      window.Tawk_API.onChatHidden = () => {
        setMaximized(false);
        try { window.Tawk_API.hideWidget(); } catch { /* ignore */ }
      };
      pushVisitorAttributes(lang);
    };

    if (!window.__tawkInjected) {
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();
      window.Tawk_API.onLoad = onReady;
      const s1 = document.createElement("script");
      s1.async = true;
      s1.src = `https://embed.tawk.to/${tawkId}/${tawkKey}`;
      s1.charset = "UTF-8";
      s1.setAttribute("crossorigin", "*");
      s1.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn("[Tawk] No se pudo cargar el widget — verifica REACT_APP_TAWK_ID y REACT_APP_TAWK_KEY");
      };
      document.body.appendChild(s1);
      window.__tawkInjected = true;
    }

    // Robust readiness poll: Tawk's onLoad isn't always reliable across versions
    // and the API methods attach asynchronously after the embed script loads.
    let tries = 0;
    const poll = setInterval(() => {
      tries += 1;
      if (window.Tawk_API && typeof window.Tawk_API.hideWidget === "function") {
        clearInterval(poll);
        onReady();
      } else if (tries > 50) {
        clearInterval(poll); // give up after ~25s
      }
    }, 500);

    return () => {
      cancelled = true;
      clearInterval(poll);
      try { window.Tawk_API?.hideWidget?.(); } catch { /* ignore */ }
    };
  }, [i18n.language]);

  // Toggle: open the chat / close it. Appearance stays the SAME either way.
  const toggleChat = () => {
    const api = window.Tawk_API;
    if (!api) return;
    try {
      if (maximized) {
        if (typeof api.minimize === "function") api.minimize();
        setMaximized(false);
        try { api.hideWidget && api.hideWidget(); } catch { /* ignore */ }
      } else if (typeof api.maximize === "function") {
        if (typeof api.showWidget === "function") api.showWidget();
        api.maximize();
        setMaximized(true);
      }
    } catch { /* ignore */ }
  };

  const FAB_SIZE = 62;
  const onPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    drag.current = {
      active: true, moved: false,
      offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
      startX: e.clientX, startY: e.clientY,
    };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    if (Math.abs(e.clientX - d.startX) > 5 || Math.abs(e.clientY - d.startY) > 5) d.moved = true;
    if (!d.moved) return;
    const nx = Math.max(6, Math.min(window.innerWidth - FAB_SIZE - 6, e.clientX - d.offsetX));
    const ny = Math.max(6, Math.min(window.innerHeight - FAB_SIZE - 6, e.clientY - d.offsetY));
    setPos({ x: nx, y: ny });
  };
  const onPointerUp = () => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    if (d.moved) {
      try { localStorage.setItem("op_chat_fab_pos", JSON.stringify(posRef.current)); } catch { /* ignore */ }
    } else {
      toggleChat();
    }
  };

  const style = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto", touchAction: "none", cursor: "grab" }
    : { touchAction: "none", cursor: "grab" };

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={style}
      className="op-chat-fab"
      aria-label="Chat de soporte (arrastrable)"
      title="Chat de soporte · arrástrame para moverme"
      data-testid="floating-chat-button"
    >
      <span className="op-chat-fab__ring" aria-hidden="true"></span>
      <MessageCircle size={26} strokeWidth={2.2} />
    </button>
  );
}
